import React, { useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import { formatWan, formatMoney, formatPct } from '../../utils/format';
import PageHeader from '../../components/PageHeader';
import './ProductSkillPage.css';

type TabKey = 'facts' | 'audience' | 'scenarios' | 'creative' | 'performance' | 'compliance';

// 页面展示名为「AI拆解」，底层数据结构仍为 productSkill
const ProductSkillPage: React.FC = () => {
  const { currentProject: project } = useProject();
  const [activeTab, setActiveTab] = useState<TabKey>('facts');
  const opt = project.optimization;

  const tabs = [
    { key: 'facts', label: '产品事实' },
    { key: 'audience', label: '人群与痛点' },
    { key: 'scenarios', label: '需求与场景' },
    { key: 'creative', label: '创意规律' },
    { key: 'performance', label: '投放规律' },
    { key: 'compliance', label: '合规限制' },
  ];

  const dimensionGroups = Array.from(new Set(opt.dimensions.map(d => d.group || '其他')));
  const conversionNoun = opt.overview.conversionLabel === '激活成本' ? '激活' : '转化';

  return (
    <div className="product-skill-page">
      <PageHeader
        title="AI拆解"
        subtitle="基于产品资料、素材和历史数据形成的项目认知——AI理解出了什么。"
      />

      {/* 分析数据概览：随当前项目数据切换（一行横排、弱边框、高信息密度） */}
      <div className="analysis-stats-bar">
        <div className="analysis-stat">
          <span className="analysis-stat-value">{project.analysisStats.hitMaterialCount} 条</span>
          <span className="analysis-stat-label">已拆解爆款素材</span>
        </div>
        <div className="analysis-stat">
          <span className="analysis-stat-value">{project.analysisStats.analyzedMaterialCount.toLocaleString()} 条</span>
          <span className="analysis-stat-label">累计分析素材</span>
        </div>
        <div className="analysis-stat">
          <span className="analysis-stat-value">{project.analysisStats.dataStartDate} – {project.analysisStats.dataEndDate}</span>
          <span className="analysis-stat-label">数据覆盖</span>
        </div>
        <div className="analysis-stat">
          <span className="analysis-stat-value">{project.analysisStats.lastUpdatedAt}</span>
          <span className="analysis-stat-label">最近更新</span>
        </div>
      </div>
      <div className="analysis-stats-note">ⓘ 分析结果会随新增素材和投放数据持续更新</div>

      <div className="tabs-container">
        <div className="tabs-header">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key as TabKey)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {activeTab === 'facts' && (
            <div className="facts-container">
              {project.productSkill.facts.map((fact, index) => (
                <div key={index} className="fact-card">
                  <div className="fact-category">{fact.category}</div>
                  <div className="fact-value">{fact.value}</div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'audience' && (
            <div className="audience-container">
              {project.productSkill.audienceSegments.map((segment, index) => (
                <div key={index} className="audience-card">
                  <h3>{segment.segmentName}</h3>
                  <p className="description">{segment.description}</p>
                  <div className="audience-info">
                    <div className="info-item">
                      <span className="label">核心需求</span>
                      <span className="value">{segment.coreNeed}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">痛点</span>
                      <span className="value">{segment.painPoint}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">触发点</span>
                      <span className="value">{segment.trigger}</span>
                    </div>
                    {segment.concern && (
                      <div className="info-item">
                        <span className="label">顾虑</span>
                        <span className="value">{segment.concern}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="pain-points-container">
                <h3>需求与痛点</h3>
                {project.productSkill.needsAndPainPoints.map((np, index) => (
                  <div key={index} className="pain-point-card">
                    <div className="np-header">
                      <span className="audience">{np.audienceSegment}</span>
                    </div>
                    <div className="np-item">
                      <span className="label">需求</span>
                      <span className="value">{np.need}</span>
                    </div>
                    <div className="np-item">
                      <span className="label">痛点</span>
                      <span className="value">{np.painPoint}</span>
                    </div>
                    <div className="np-item">
                      <span className="label">触发点</span>
                      <span className="value">{np.trigger}</span>
                    </div>
                    {np.concern && (
                      <div className="np-item">
                        <span className="label">顾虑</span>
                        <span className="value">{np.concern}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'scenarios' && (
            <div className="scenarios-container">
              {project.productSkill.scenarios.map((scenario, index) => (
                <div key={index} className="scenario-card">
                  <h3>{scenario.name}</h3>
                  <div className="scenario-info">
                    <div className="info-row">
                      <span className="label">描述</span>
                      <span className="value">{scenario.description}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">用户场景</span>
                      <span className="value">{scenario.userContext}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">产品入口</span>
                      <span className="value">{scenario.productEntrance}</span>
                    </div>
                  </div>
                </div>
              ))}

              <div className="benefits-container">
                <h3>利益信息</h3>
                {project.productSkill.benefitMessages.map((benefit, index) => (
                  <div key={index} className="benefit-card">
                    <div className="benefit-audience">{benefit.audienceSegment}</div>
                    <div className="benefit-info">
                      <div className="info-row">
                        <span className="label">核心利益</span>
                        <span className="value">{benefit.benefit}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">沟通角度</span>
                        <span className="value">{benefit.messagingAngle}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">产品承接</span>
                        <span className="value">{benefit.productPlacement}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'creative' && (
            <div className="creative-container">
              {project.productSkill.creativeRecommendations.map((rec, index) => (
                <div key={index} className="creative-card">
                  <div className="creative-card-head">
                    <span className="creative-index">{String(index + 1).padStart(2, '0')}</span>
                    <h3>{rec.name}</h3>
                    {rec.contentFormat && <span className="status-badge info">{rec.contentFormat}</span>}
                  </div>
                  <p className="creative-conclusion">
                    <span className="creative-field-label">一句结论</span>
                    {rec.description}
                  </p>
                  {rec.hook && (
                    <blockquote className="creative-hook">
                      <span className="creative-field-label">前三秒 Hook</span>
                      {rec.hook}
                    </blockquote>
                  )}
                  <div className="creative-meta-grid">
                    {rec.character && (
                      <div className="creative-meta"><span>适用人群</span>{rec.character}{rec.characterRelationship ? `（${rec.characterRelationship}）` : ''}</div>
                    )}
                    {rec.scene && (
                      <div className="creative-meta"><span>场景</span>{rec.scene}</div>
                    )}
                    {rec.storyDirection && (
                      <div className="creative-meta"><span>历史依据</span>{rec.storyDirection}</div>
                    )}
                    {rec.visualStyle && (
                      <div className="creative-meta"><span>视觉风格</span>{rec.visualStyle}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="performance-container">
              <div className="perf-section">
                <h3>素材标签表现</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>标签</th>
                      <th className="num">素材数</th>
                      <th className="num">消耗</th>
                      <th className="num">消耗占比</th>
                      <th className="num">{opt.overview.conversionLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opt.tagStats.map((tag, index) => (
                      <tr key={index}>
                        <td>{tag.label}{tag.note ? <span className="cell-note">（{tag.note}）</span> : null}</td>
                        <td className="num">{tag.materialCount}</td>
                        <td className="num">{formatWan(tag.spend)}</td>
                        <td className="num">{formatPct(tag.spendSharePct)}</td>
                        <td className="num">{tag.costPerConversion === null ? '—' : formatMoney(tag.costPerConversion)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="perf-section">
                <h3>维度对比</h3>
                {dimensionGroups.map(group => (
                  <div key={group} className="perf-group">
                    <h4>{group}</h4>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>名称</th>
                          <th className="num">消耗</th>
                          <th className="num">消耗占比</th>
                          <th className="num">{opt.overview.conversionLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {opt.dimensions.filter(d => (d.group || '其他') === group).map((dim, index) => (
                          <tr key={index}>
                            <td>{dim.name}{dim.note ? <span className="cell-note">（{dim.note}）</span> : null}</td>
                            <td className="num">{formatWan(dim.spend)}</td>
                            <td className="num">{formatPct(dim.spendSharePct)}</td>
                            <td className="num">{dim.costPerConversion === null || dim.costPerConversion === undefined ? '—' : formatMoney(dim.costPerConversion)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              <div className="perf-columns">
                <div className="perf-section">
                  <h3>跑量 Top 素材</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>素材</th>
                        <th className="num">消耗</th>
                        <th className="num">{conversionNoun}</th>
                        <th className="num">{opt.overview.conversionLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opt.topMaterials.map((m, index) => (
                        <tr key={index}>
                          <td>{m.name}</td>
                          <td>{formatWan(m.spend)}</td>
                          <td className="num">{m.conversions}</td>
                          <td className="num">{formatMoney(m.costPerConversion)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="perf-section">
                  <h3>成本最优素材</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>素材</th>
                        <th className="num">消耗</th>
                        <th className="num">{conversionNoun}</th>
                        <th className="num">{opt.overview.conversionLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opt.bestCpaMaterials.map((m, index) => (
                        <tr key={index}>
                          <td>{m.name}</td>
                          <td>{formatWan(m.spend)}</td>
                          <td className="num">{m.conversions}</td>
                          <td className="num">{formatMoney(m.costPerConversion)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="perf-columns">
                <div className="perf-section">
                  <h3>有效方向</h3>
                  <ul className="direction-list effective">
                    {opt.effectiveDirections.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="perf-section">
                  <h3>低效方向</h3>
                  <ul className="direction-list weak">
                    {opt.weakDirections.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {opt.historyNote && (
                <div className="perf-section">
                  <h3>历史复盘补充</h3>
                  <p className="history-note">{opt.historyNote}</p>
                </div>
              )}

              <p className="data-note">{opt.dataSourceNote}</p>
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="compliance-container">
              <div className="compliance-rules">
                <h3>合规规则</h3>
                <ul>
                  {project.productSkill.compliance.map((rule, index) => (
                    <li key={index} className={`rule-item severity-${rule.severity.toLowerCase()}`}>
                      <div className="rule-header">
                        <span className="rule-type">{rule.ruleType}</span>
                        <span className="rule-severity">{rule.severity}</span>
                      </div>
                      <div className="rule-content">{rule.content}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="compliance-summary">
                <h3>表达限制</h3>
                <ul>
                  {project.productSkill.complianceRules.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSkillPage;
